/** @file Sets how a direction plays: authored, mirror (flip its counterpart) or mirrorFix (flip + fix). */
import { z } from 'zod';
import type { Draft } from 'immer';
import { names } from '@pose2d/format/names';
import { mirrorAsymmetry } from '@pose2d/format/doc/mirrorAsymmetry';
import { prim } from '@pose2d/format/doc/schemaParts/primitives';
import { throwError } from '@pose2d/format/throwError';
import type { Dir, DirectionEntry, DirectionsDoc } from '@pose2d/format/types';
import { defineCommand } from '../defineCommand';
import { allowedDirs } from './allowedDirs';
import { COUNTERPART } from './counterpart';
import { directionsDraft } from './directionsDraft';

type DirSet = Draft<DirectionsDoc>;
type ModeInput = { dir: Dir; mode: DirectionEntry['mode']; source?: Dir; flipX?: boolean };

/** Throws VALIDATION at `path` with `hint`. */
function fail(path: string, hint: string): never {
  return throwError('VALIDATION', undefined, { path, hint });
}

/** The mirrored direction that takes its animations from `dir`, if any. */
function mirrorOf(set: DirSet, dir: Dir): Dir | undefined {
  return names.DIRS.find((d) => {
    const e = set.entries[d];
    return e !== undefined && e.mode !== 'authored' && e.source === dir;
  });
}

/** The source a mirrored `dir` must use: its horizontal counterpart, which must already be authored. */
function mirrorSource(set: DirSet, input: ModeInput): Dir {
  const expected = COUNTERPART[input.dir] ?? fail('mode', `${input.dir} has no horizontal counterpart; use authored`);
  if (input.source !== undefined && input.source !== expected) fail('source', `${input.dir} may only mirror ${expected}`);
  if (set.entries[expected]?.mode !== 'authored') fail('source', `make ${expected} authored first (direction.setMode { dir: "${expected}", mode: "authored" })`);
  return expected;
}

/** A direction outside a 4-set: which layout the set has, its directions, and the two ways to get `dir` (CB-07 S7). */
function outsideSet(set: DirSet, dir: Dir): never {
  const layout = set.layout4 ?? 'diagonal';
  const other = layout === 'diagonal' ? 'cardinal' : 'diagonal';
  return fail('dir', `${dir} is not part of this 4-direction set: it is ${layout} (${allowedDirs(4, layout).join(', ')}); for ${dir} use direction.setLayout4 { layout4: "${other}" } or direction.setCount { count: 8 }`);
}

/** Validates the request against the set; returns the source of a mirrored mode, undefined for authored. */
function checkRequest(set: DirSet, input: ModeInput): Dir | undefined {
  if (!allowedDirs(set.count, set.layout4).includes(input.dir)) outsideSet(set, input.dir);
  if (input.mode === 'authored') {
    if (input.source !== undefined) fail('source', 'authored directions have no source');
    return undefined;
  }
  if (input.flipX !== undefined) fail('flipX', 'flipX applies to authored directions; mirrored ones always flip');
  const user = mirrorOf(set, input.dir);
  if (user !== undefined) fail('mode', `${user} mirrors ${input.dir}; change ${user} first`);
  return mirrorSource(set, input);
}

/** True when writeEntry would leave `entry` exactly as it is (W2-31 no-op). */
function unchanged(entry: DirectionEntry | undefined, input: ModeInput, source: Dir | undefined): boolean {
  if (entry?.mode !== input.mode || entry.source !== source) return false;
  if (input.mode !== 'mirrorFix' && entry.fix !== undefined) return false;
  return input.mode === 'authored' ? input.flipX === undefined || input.flipX === entry.flipX : entry.flipX === undefined;
}

/** Rewrites (or creates) the entry in place: source only when mirrored, fix only for mirrorFix, flipX only when authored. */
function writeEntry(set: DirSet, input: ModeInput, source: Dir | undefined): void {
  const entry = (set.entries[input.dir] ??= { mode: input.mode });
  entry.mode = input.mode;
  if (source === undefined) delete entry.source;
  else entry.source = source;
  if (input.mode !== 'mirrorFix') delete entry.fix;
  if (input.mode !== 'authored') delete entry.flipX;
  else if (input.flipX !== undefined) entry.flipX = input.flipX;
}

/** direction.setMode: validates §5.8, rewrites the entry unless it already matches, returns its MIRROR_ASYMMETRY warnings. */
export const command = defineCommand({
  name: 'direction.setMode',
  description:
    'Set how direction `dir` plays: authored (its own <base>@<DIR> animations; flipX optional), mirror (flip its horizontal counterpart, which must be authored; source defaults to it) or mirrorFix (mirror plus direction.setFix fixes); MIRROR_ASYMMETRY warnings for this direction are returned.',
  input: z.object({ dir: prim.dir, mode: z.enum(['authored', 'mirror', 'mirrorFix']), source: prim.dir.exactOptional(), flipX: z.boolean().exactOptional() }).strict(),
  apply(draft, input, ctx) {
    const set = directionsDraft(draft, 'dir');
    const source = checkRequest(set, input);
    if (!unchanged(set.entries[input.dir], input, source)) {
      writeEntry(set, input, source);
      draft.meta.updatedAt = ctx.now;
    }
    for (const w of mirrorAsymmetry(draft)) if (w.params?.dir === input.dir) ctx.warnings.push(w);
  },
});
